import React, { useState, useRef, useEffect } from 'react';
import { STATUS_CATEGORIES } from '../../../FlowchartView/TaskNode/constants';
import { chooseOnPress } from '../hooks/paintChoice';
import { canDeleteStatus, sortStatusesByCategory } from '../../../FlowchartView/utils/statusUtils';

/**
 * StatusOptionsList Component
 * 
 * Renders the list of status options with edit/delete actions.
 * For tasks with nested sprints, shows an "Auto" option to enable automatic status sync.
 */
const StatusOptionsList = ({
  statuses,
  currentStatus,
  onSelect,
  onAddStatus,
  onDeleteStatus,
  onRenameStatus,
  onShowAddForm,
  onRequestDelete,
  // Nested sprint props
  hasNestedSprint = false,
  isAutoSyncEnabled = false,
  nestedSprintProgress = 0,
  onSelectAuto,
  // Lock state - when locked, non-"not_started" statuses will trigger warning
  isLocked = false,
}) => {
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [editingStatusName, setEditingStatusName] = useState('');
  const editInputRef = useRef(null);

  // Focus input when editing a status
  useEffect(() => {
    if (editingStatusId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingStatusId]);

  const handleStartEdit = (e, status) => {
    e.stopPropagation();
    setEditingStatusId(status.id);
    setEditingStatusName(status.name);
  };

  const handleSaveEdit = () => {
    if (editingStatusId && editingStatusName.trim() && onRenameStatus) {
      onRenameStatus(editingStatusId, editingStatusName.trim());
    }
    setEditingStatusId(null);
    setEditingStatusName('');
  };

  const handleCancelEdit = () => {
    setEditingStatusId(null);
    setEditingStatusName('');
  };

  const handleDeleteClick = (e, statusId) => {
    e.stopPropagation();
    
    const statusToDelete = statuses.find(s => s.id === statusId);
    const statusName = statusToDelete?.name || 'this status';
    
    const deleteCheck = canDeleteStatus(statusId, statuses);
    if (!deleteCheck.canDelete) {
      onRequestDelete(statusId, statusName, deleteCheck.reason);
      return;
    }
    
    onRequestDelete(statusId, statusName, null);
  };

  return (
    <>
      {/* Auto option for tasks with nested sprints */}
      {hasNestedSprint && onSelectAuto && (
        <>
          <div
            className={`status-dropdown__option status-dropdown__option--auto ${isAutoSyncEnabled ? 'status-dropdown__option--active' : ''}`}
            onMouseDown={(event) => chooseOnPress(event, onSelectAuto)}
          >
            <span className="status-dropdown__auto-icon">⚡</span>
            <span className="status-dropdown__option-name">Auto</span>
            <span className="status-dropdown__option-category status-dropdown__option-category--auto">
              {nestedSprintProgress}% complete
            </span>
          </div>
          <div className="status-dropdown__divider" />
        </>
      )}
      
      {sortStatusesByCategory(statuses).map((status) => {
        const deleteCheck = canDeleteStatus(status.id, statuses);
        const isEditing = editingStatusId === status.id;
        const isNotStartedCategory = status.category === 'not_started';
        const wouldTriggerWarning = isLocked && !isNotStartedCategory;
        
        return (
          <div
            key={status.id}
            className={`status-dropdown__option ${currentStatus === status.id ? 'status-dropdown__option--active' : ''} ${isEditing ? 'status-dropdown__option--editing' : ''} ${wouldTriggerWarning ? 'status-dropdown__option--warning' : ''}`}
            onMouseDown={(event) => {
              if (isEditing || event.target.closest('.status-dropdown__actions, input')) return;
              chooseOnPress(event, () => onSelect(status.id));
            }}
            title={wouldTriggerWarning ? 'This status requires completing dependencies first' : undefined}
          >
            <span className="status-dropdown__option-dot" style={{ background: status.color }} />
            
            {isEditing ? (
              <input
                ref={editInputRef}
                type="text"
                className="status-dropdown__edit-input"
                value={editingStatusName}
                onChange={(e) => setEditingStatusName(e.target.value)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === 'Enter') handleSaveEdit();
                  if (e.key === 'Escape') handleCancelEdit();
                }}
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <span className="status-dropdown__option-name">{status.name}</span>
            )}
            
            <span className="status-dropdown__option-category">
              {STATUS_CATEGORIES[status.category]?.label}
            </span>
            
            <div className="status-dropdown__actions">
              {isEditing ? (
                <>
                  <button
                    className="status-dropdown__action status-dropdown__action--save"
                    onClick={(e) => { e.stopPropagation(); handleSaveEdit(); }}
                    title="Save"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 6L9 17l-5-5"/>
                    </svg>
                  </button>
                  <button
                    className="status-dropdown__action status-dropdown__action--cancel"
                    onClick={(e) => { e.stopPropagation(); handleCancelEdit(); }}
                    title="Cancel"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 6L6 18M6 6l12 12"/>
                    </svg>
                  </button>
                </>
              ) : (
                <>
                  {onRenameStatus && (
                    <button
                      className="status-dropdown__action status-dropdown__action--edit"
                      onClick={(e) => handleStartEdit(e, status)}
                      title="Rename status"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                    </button>
                  )}
                  {onDeleteStatus && (
                    <button
                      className={`status-dropdown__action status-dropdown__action--delete ${!deleteCheck.canDelete ? 'status-dropdown__action--disabled' : ''}`}
                      onClick={(e) => handleDeleteClick(e, status.id)}
                      title={deleteCheck.canDelete ? 'Delete status' : deleteCheck.reason}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 6L6 18M6 6l12 12"/>
                      </svg>
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
      
      {onAddStatus && (
        <>
          <div className="status-dropdown__divider" />
          <button
            className="status-dropdown__option status-dropdown__option--add"
            onClick={onShowAddForm}
          >
            <span className="status-dropdown__add-icon">+</span>
            Add Status
          </button>
        </>
      )}
    </>
  );
};

export default StatusOptionsList;
